import { Box, Typography, List, ListItem } from "@mui/material";
import { Formik } from "formik";
import { ReactElement, useState } from "react";
import * as Yup from "yup";

import {
  UploadCampaignContactsMutation,
  UploadCampaignContactsMutationVariables,
  UploadType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { useCsvUpload } from "~frontend/components/forms/CsvUpload/useCsvUpload";
import { UPLOAD_CAMPAIGN_CONTACTS } from "~frontend/graphql/campaigns";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import useMutation from "~frontend/util/useMutation";
import { useStaffCsvFilenameValidator } from "~frontend/util/useStaffCsvFilenameValidator";

import { CampaignForEditor } from "../../types";
import ContactImportFormSubmitButtons from "../ContactImportFormSubmitButtons";

export default function CsvUploadForm({
  campaign,
  resetContactSourceSettings,
}: {
  campaign: CampaignForEditor;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const contactLimit = useFeatureFlag("contactLimit");

  const [startImport] = useMutation<
    UploadCampaignContactsMutation,
    UploadCampaignContactsMutationVariables
  >(UPLOAD_CAMPAIGN_CONTACTS, {
    onErrorSnackbarMessage: "Error starting import",
  });
  const [overwritingCsv, setOverwritingCsv] = useState(false);

  const ngpVanCampaignIntegrationEnabled = !!(
    campaign.integration && campaign.integration.service == "NGPVAN"
  );

  const validator = useStaffCsvFilenameValidator();

  const {
    renderCsvUpload,
    startCsvUpload,
    csvState,
    setProcessingState,
    isOverwritingInProgress: isCsvDirty,
    onClearFile,
  } = useCsvUpload({
    uploadType: UploadType.ContactCsv,
    overwriting: overwritingCsv,
    onChangeOverwriting: setOverwritingCsv,
    doNotRenderFileWhileImporting: true,
    renderProcessingState: () => {
      if (csvState === "IMPORTING_CSV" || csvState === "UPLOADED_CSV") {
        return <Loading />;
      }
      return null;
    },
    validCsvFilename: validator,
    columnPickerFields: ngpVanCampaignIntegrationEnabled
      ? [
          {
            name: "vanIdColumn",
            label: "VAN ID Column",
            autoMatchColumns: ["vanid", "externalid"],
          },
        ]
      : [],
  });

  const initialValues = ngpVanCampaignIntegrationEnabled
    ? { phoneNumberColumn: "", vanIdColumn: "" }
    : { phoneNumberColumn: "" };

  const validationSchema = ngpVanCampaignIntegrationEnabled
    ? Yup.object().shape({
        phoneNumberColumn: Yup.string().required(
          "Please select a phone number column"
        ),
        vanIdColumn: Yup.string().required("Please select a VAN column"),
      })
    : Yup.object().shape({
        phoneNumberColumn: Yup.string().required(
          "Please select a phone number column"
        ),
      });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        const uploadMetadata = await startCsvUpload({
          phoneNumberColumn: values.phoneNumberColumn,
          ...(ngpVanCampaignIntegrationEnabled
            ? { vanIdColumn: values.vanIdColumn }
            : {}),
        });
        if (!uploadMetadata) {
          setSubmitting(false);
          return;
        }

        setProcessingState("IMPORTING_CSV");

        const { errors } = await startImport({
          variables: {
            input: {
              campaignId: campaign.id,
              uploadToken: uploadMetadata.uploadToken,
              phoneNumberColumn: values.phoneNumberColumn,
              vanIdColumn: values.vanIdColumn ?? null,
              estimatedRowCount: uploadMetadata.estimatedRowCount,
            },
          },
        });

        if (errors) {
          setSubmitting(false);
          setProcessingState("CSV_IMPORT_FAILED");
          setOverwritingCsv(true);
          return;
        }

        setSubmitting(false);
        setProcessingState("CSV_IMPORTED");
      }}
    >
      {({ setFieldValue, dirty }) => {
        return (
          <>
            <RouteLeavingGuard when={isCsvDirty || dirty} />

            {csvState === "NO_CSV" && (
              <Box sx={{ marginBottom: "24px" }}>
                <Typography variant="body1">
                  Use the following instructions to make sure your upload is
                  successful:
                </Typography>
                <Typography variant="body2" component="div">
                  <List
                    sx={{
                      listStyleType: "disc",
                      pl: "24px",
                      "& .MuiListItem-root": {
                        ":not(:last-child)": {
                          marginBottom: "10px",
                        },
                        padding: "0px",
                        display: "list-item",
                      },
                    }}
                  >
                    <ListItem>
                      Your uploaded file must be in .csv format and contain
                      columns for phone number and first name.
                    </ListItem>
                    <ListItem>
                      To add contact fields to your scripts, add the additional
                      data to your CSV with a column name of your choosing.
                    </ListItem>
                    <ListItem>
                      Note: You cannot upload more than{" "}
                      {contactLimit.toLocaleString()} contacts.
                    </ListItem>
                  </List>
                </Typography>
              </Box>
            )}

            {renderCsvUpload(setFieldValue)}
            {!["NO_CSV", "PARSING_CSV", "PARSED_CSV_ERROR"].includes(
              csvState
            ) && (
              <FlexBox justifyContent="flex-end">
                <ContactImportFormSubmitButtons
                  resetContactSourceSettings={
                    resetContactSourceSettings
                      ? () => {
                          resetContactSourceSettings();
                          setOverwritingCsv(false);
                          onClearFile();
                        }
                      : null
                  }
                />
              </FlexBox>
            )}
          </>
        );
      }}
    </Formik>
  );
}
